// 共通 色ユーティリティ

import { getGoogleColors, getOutlookColors } from '@/services/colorService';
import { ColorDefinition } from '@/types/ColorType';
import i18n from '@/i18n';

// 色選択用（現状はカレンダーの型と同一）
export type ColorOption = {
    value: string;
    label: string;
    colorHex?: string;
    isDivider?: boolean;
    originalLabel?: string;
};

const resolveTranslator = (languageTag?: string) =>
    i18n.getFixedT(languageTag ?? i18n.language, 'common');

/** 色選択のデフォルト値（指定なし） */
export const getDefaultColorOption = (languageTag?: string): ColorOption => {
    const t = resolveTranslator(languageTag);
    const label = t('options.none');
    return { value: '', label, originalLabel: label };
};

/**
 * ColorDefinitionをColorOptionに変換
 */
const convertToColorOption = (color: ColorDefinition): ColorOption => {
    return {
        value: color.key,
        label: color.label,
        colorHex: color.hexColor,
        originalLabel: color.originalLabel ?? color.label,
    };
};

/**
 * 色選択肢を動的に取得（APIベース）
 */
export const createColorOptionsFromApi = async (languageTag?: string): Promise<ColorOption[]> => {
    try {
        const colors = await getGoogleColors(languageTag);
        const t = resolveTranslator(languageTag);
        const options: ColorOption[] = [
            getDefaultColorOption(languageTag),
            { value: 'all', label: t('options.all'), originalLabel: t('options.all') },
            ...colors.map(convertToColorOption),
        ];
        return options;
    } catch (error) {
        console.error('Failed to load color options:', error);
        const t = resolveTranslator(languageTag);
        // フォールバック: 空の選択肢を返す
        return [
            getDefaultColorOption(languageTag),
            { value: 'all', label: t('options.all'), originalLabel: t('options.all') },
        ];
    }
};

// Google既定色（イベント色）- APIベース
export const createGoogleColorOptions = async (languageTag?: string): Promise<ColorOption[]> => {
    return await createColorOptionsFromApi(languageTag);
};

// Outlook既定色（代表的プリセット）- APIベース
export const createOutlookColorOptions = async (
    languageTag?: string,
): Promise<ColorOption[]> => {
    try {
        const colors = await getOutlookColors(languageTag);
        const t = resolveTranslator(languageTag);
        // 代表的な6色のみを表示（UI選択肢用）
        const representativeKeys = [
            'o-preset1',
            'o-preset4',
            'o-preset5',
            'o-preset9',
            'o-preset10',
            'o-preset23',
            'o-uncategorized',
        ];
        const filteredColors = colors.filter((color) => representativeKeys.includes(color.key));

        const options: ColorOption[] = [
            getDefaultColorOption(languageTag),
            { value: 'all', label: t('options.all'), originalLabel: t('options.all') },
            ...filteredColors.map(convertToColorOption),
        ];
        return options;
    } catch (error) {
        console.error('Failed to load Outlook color options:', error);
        const t = resolveTranslator(languageTag);
        // フォールバック: 空の選択肢を返す
        return [
            getDefaultColorOption(languageTag),
            { value: 'all', label: t('options.all'), originalLabel: t('options.all') },
        ];
    }
};

/**
 * プロバイダー名から色オプションの基本リストを取得（非同期）
 */
export const getBaseColorOptionsByProvider = async (
    providerName?: string,
    languageTag?: string,
): Promise<ColorOption[]> => {
    if (!providerName) return await createGoogleColorOptions(languageTag);
    const name = providerName.toLowerCase();
    if (name.includes('azure') || name.includes('microsoft') || name.includes('outlook')) {
        return await createOutlookColorOptions(languageTag);
    }
    return await createGoogleColorOptions(languageTag);
};

/**
 * 並び替え: 指定なし → 仕切り → すべて → 仕切り → 使用順(MRU) → 仕切り → その他
 */
export const applyColorOrderWithDividers = (
    base: ColorOption[],
    order: string[] = [],
): ColorOption[] => {
    const t = resolveTranslator();
    const none =
        base.find((o) => o.value === '') ||
        getDefaultColorOption();
    const all =
        base.find((o) => o.value === 'all') ||
        ({ value: 'all', label: t('options.all'), originalLabel: t('options.all') } as ColorOption);
    const colorMap = new Map(base.map((o) => [o.value, o] as const));

    const used: ColorOption[] = [];
    const seen = new Set<string>();
    for (const v of order) {
        if (!v || v === 'all') continue;
        const opt = colorMap.get(v);
        if (opt && !seen.has(v)) {
            used.push(opt);
            seen.add(v);
        }
    }
    const rest = base.filter((o) => o.value !== '' && o.value !== 'all' && !seen.has(o.value));

    const list: ColorOption[] = [];
    list.push(none);
    list.push({ value: `__div1__`, label: '', isDivider: true });
    list.push(all);
    list.push({ value: `__div2__`, label: '', isDivider: true });
    if (used.length > 0) {
        list.push(...used);
        list.push({ value: `__div3__`, label: '', isDivider: true });
    }
    list.push(...rest);
    return list;
};
